import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FeatureAccessService } from '@app/services/feature-access/feature-access.service';
import { LockableTabItem } from '@app/services/feature-access/feature-access.model';
import {
  AbstractControl,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { CheckoutReducer } from '@app/pages/checkout-app/checkout-store/reducers';
import { ShopService } from '@app/services/shops/shop.service';
import { onDestroyComplete } from '@app/sharedModule/decorators/on-destroy-unsubscribe.decorator';
import { Store } from '@ngrx/store';
import {
  CheckoutExtensionCommonConfig,
  CheckoutExtensionUI,
  CheckoutUpsellExtension,
  ExtensionComponentType,
  ICheckoutExtensions,
  UpsellFreeShippingThresholdReplaceValues,
  GiftUnlockExtension,
} from '@pdqdev/pdq-shared/dist/interfaces/ICheckoutExtensions';
import { IUpsellThresholdTypes } from '@pdqdev/pdq-shared/dist/interfaces/IThresholdTypes';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ViewMode } from '../../../models';
import { CheckoutActions } from '@app/pages/checkout-app/checkout-store/actions';
import { currencyCodes } from '@pdqdev/pdq-shared/dist/utils/currency-utils';
import { generateRandomString } from '@utils/string-utils';
import { getExtensionRules } from '../../../shared/all-extension-rules';
import { RulesDynamicFormService } from '@app/sharedModule/components/rules-dynamic-form/rules-dynamic-form.service';
import { ExtensionsService } from '@pages/checkout-app/services/extensions/extensions.service';
import { CheckoutUpsellTimerComponent } from './components/checkout-upsell-timer/checkout-upsell-timer.component';
import {
  createProductReviewsFormGroup,
  createRuleGroupFormGroup,
  DEFAULT_RULE_GROUP,
  removeDiscountRulesWithoutEntity,
} from './models';
import {
  AutonomousDiscountConfiguration,
  autonomousDiscountValidator,
  createAllowedDiscountValueControl,
  DiscountMode,
  isAutonomousGroup,
  prepareDiscountConfigurationForSave,
  refreshAllowedDiscountValueValidity,
  resolveAllowedDiscountValuesOnEnable,
  resolveAutonomousFallbackValue,
  StaticDiscountSnapshot,
  resolveShowDiscountSection,
  resolveShowLegacyDiscountControls,
  resolveShowStaticDiscountControls,
} from './autonomous-discount';
import { FeatureFlag } from '@app/core/services/feature-flags/feature-flag.model';
import { FeatureFlagService } from '@app/core/services/feature-flags/feature-flag.service';
import { AnalyticsService } from '@app/core/services/analytics/analytics.service';
import { AnalyticsEvent } from '@app/core/services/analytics/analytics.events';
import PlacementMode = CheckoutExtensionCommonConfig.PlacementMode;
import { CheckoutUpsellGoalsComponent } from './components/checkout-upsell-goals/checkout-upsell-goals.component';
import Goal = CheckoutUpsellExtension.Goal;
import GoalsDisplayMode = CheckoutUpsellExtension.GoalsDisplayMode;
import UpsellTextType = CheckoutUpsellExtension.UpsellTextType;

const STYLING_GROUP_NAMES = ['mainBackgroundStyling', 'cardBackgroundStyling', 'imageStyling'];

// stamped only on create and duplicate, so the extension knows this template carries explicit styling
const CHECKOUT_UPSELL_STYLE_VERSION = 2;

// what the extension renders for a missing field — seeding these keeps a pre-styleVersion upsell's look on save
// imageStyling is left out: the extension only pads an image that has a border
const LEGACY_STYLE_FALLBACKS: Record<string, Record<string, string>> = {
  mainBackgroundStyling: { padding: CheckoutExtensionUI.Padding.BASE },
  cardBackgroundStyling: {
    border: CheckoutExtensionUI.BorderType.BASE,
    cornerRadius: CheckoutExtensionUI.CornerRadius.LARGE,
    padding: CheckoutExtensionUI.Padding.BASE,
  },
};

enum CheckoutUpsellTab {
  SETTINGS = 'settings',
  DISPLAY_CONDITIONS = 'display_conditions',
  GOALS = 'goals',
  PRODUCT_SELECTION = 'product_selection',
  TIMER = 'timer',
}

@Component({
  selector: 'checkout-upsell',
  templateUrl: './checkout-upsell.component.html',
  styleUrls: ['./checkout-upsell.component.scss'],
  standalone: false,
})
export class CheckoutUpsellComponent implements OnInit, OnDestroy {
  @ViewChild(CheckoutUpsellTimerComponent) timerComponent: CheckoutUpsellTimerComponent;
  @ViewChild(CheckoutUpsellGoalsComponent) goalsComponent?: CheckoutUpsellGoalsComponent;
  @onDestroyComplete() destroy$ = new Subject();

  loading$ = this.store.select(CheckoutReducer.selectIsLoading);
  loadedExtension$ = this.store.select(CheckoutReducer.selectActiveExtension);
  loadedExtension: ICheckoutExtensions;
  showDiscountSection = false;
  showLegacyDiscountControls = false;
  showStaticDiscountControls = true;
  autonomousDiscountFlagOn = false;
  private staticDiscountSnapshot: StaticDiscountSnapshot | null = null;
  currentTimerConfiguration = null;
  currencyCodes = currencyCodes;

  tabs: LockableTabItem<CheckoutUpsellTab>[] = [
    { label: 'Settings', value: CheckoutUpsellTab.SETTINGS },
    { label: 'Goals', value: CheckoutUpsellTab.GOALS },
    { label: 'Product Selection', value: CheckoutUpsellTab.PRODUCT_SELECTION },
    { label: 'Timer', value: CheckoutUpsellTab.TIMER },
    { label: 'Display Conditions', value: CheckoutUpsellTab.DISPLAY_CONDITIONS },
  ];
  CheckoutUpsellTab = CheckoutUpsellTab;
  activeTab = CheckoutUpsellTab.SETTINGS;
  formGroup: UntypedFormGroup;
  shopCountry: string;
  isPreviewThresholdMode = true;
  shopCurrencySymbol: string;
  entities;
  readonly formArrayFieldName = 'restrictionsRules';
  restrictionsRules = getExtensionRules();
  giftUnlockExtensionsByOfferType: Record<string, { label: string; value: number }[]> = {};
  giftUnlockExtensionsMap: Map<number, ICheckoutExtensions> = new Map();
  goals: Goal[] = [];
  previewedGoalIndex: number = 0;
  previewedTextType: UpsellTextType = UpsellTextType.Threshold;

  constructor(
    public shopService: ShopService,
    private store: Store<CheckoutReducer.State>,
    private rulesDynamicFormService: RulesDynamicFormService,
    private extensionsService: ExtensionsService,
    private cdr: ChangeDetectorRef,
    private featureAccess: FeatureAccessService,
    private featureFlags: FeatureFlagService,
    private analytics: AnalyticsService,
  ) {}

  ngOnDestroy() {}

  isTabDisabled(tab: LockableTabItem<CheckoutUpsellTab>): boolean {
    return tab.disabled || (tab.value === CheckoutUpsellTab.GOALS && this.isGoalsTabDisabled);
  }

  onTabChange(event: CheckoutUpsellTab) {
    if (this.activeTab === CheckoutUpsellTab.TIMER && this.timerComponent) {
      this.currentTimerConfiguration = this.timerComponent.getTimerConfiguration();
    }

    this.activeTab = event;
  }

  get freeThresholdsFormArray() {
    return this.formGroup.get('configure.freeThresholds') as UntypedFormArray;
  }

  get defaultCountry() {
    return this.shopCountry ?? 'US';
  }

  get defaultCurrency() {
    return this.shopService.shopCurrency ?? 'USD';
  }

  get defaultCurrencySymbol() {
    return this.shopService.shopCurrencySymbol ?? '$';
  }

  get additionalDisplayTextsFormArray(): UntypedFormArray {
    return this.formGroup.get('settings.additionalDisplayTexts') as UntypedFormArray;
  }

  get allowedDiscountValuesFormArray(): UntypedFormArray {
    return this.formGroup.get('discountConfiguration.allowedDiscountValues') as UntypedFormArray;
  }

  get isAutonomousDiscount(): boolean {
    return isAutonomousGroup(this.formGroup?.get('discountConfiguration'));
  }

  get isGoalsTabDisabled(): boolean {
    return this.formGroup?.get('settings.thresholdType')?.value === IUpsellThresholdTypes.NoThreshold;
  }

  ngOnInit() {
    this.tabs = this.featureAccess.lockTabOnFreePlan(this.tabs, CheckoutUpsellTab.DISPLAY_CONDITIONS);
    this.extensionsService.setCurrentExtensionType(ExtensionComponentType.CHECKOUT_UPSELL);
    this.extensionsService.save$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleSave();
    });

    this.shopService.primaryLocation$.pipe(takeUntil(this.destroy$)).subscribe((primaryLocation) => {
      this.shopCountry = primaryLocation?.countryCode;
    });
    this.shopCurrencySymbol = this.shopService.shopCurrencySymbol;
    this.entities = this.restrictionsRules.map((r) => r.entity);
    this.initForm();
    this.loadedExtension$.pipe(takeUntil(this.destroy$)).subscribe((extension: ICheckoutExtensions) => {
      this.loadedExtension = extension;
      if (extension) {
        this.currentTimerConfiguration = extension.template?.timerConfiguration ?? null;
        this.initForm(extension.template, extension.usageToken);
      }
      this.refreshDiscountSectionVisibility();
    });

    this.featureFlags
      .isEnabled$(FeatureFlag.AutonomousUpsellDiscount)
      .pipe(takeUntil(this.destroy$))
      .subscribe((enabled) => {
        this.autonomousDiscountFlagOn = enabled;
        this.refreshDiscountSectionVisibility();
      });
    this.formGroup.statusChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.extensionsService.setFormValid(this.formGroup.valid);
    });
    this.extensionsService.setFormValid(this.formGroup.valid);
  }

  dispatchExampleViewValues(values) {
    const mode = this.loadedExtension ? ViewMode.EDIT : ViewMode.NEW;
    const timerConfiguration = this.timerComponent?.getTimerConfiguration() ?? this.currentTimerConfiguration;
    const data = {
      [ExtensionComponentType.CHECKOUT_UPSELL]: {
        mode,
        template: { ...values, timerConfiguration },
        previewMode: this.isPreviewThresholdMode ? 'threshold' : 'upsell',
        previewedGoalIndex: this.previewedGoalIndex,
        previewedTextType: this.previewedTextType,
      },
    };
    this.store.dispatch(CheckoutActions.liveExampleTemplateView({ data }));
  }

  initForm(loadedValues?: any, usageToken?: string) {
    this.formGroup = new UntypedFormGroup({
      usageToken: new UntypedFormControl(usageToken ?? 'checkout_upsell_' + generateRandomString(12)),
      internalName: new UntypedFormControl(loadedValues?.internalName ?? 'Upsell', Validators.required),
      placementMode: new UntypedFormControl(loadedValues?.placementMode ?? PlacementMode.STANDALONE),
      applyDiscountToRecommendedProducts: new UntypedFormControl(
        loadedValues?.applyDiscountToRecommendedProducts ?? false,
      ),
      discountConfiguration: new UntypedFormGroup(
        {
          discountType: new UntypedFormControl(
            loadedValues?.discountConfiguration?.discountType ?? CheckoutUpsellExtension.DiscountType.FixedAmount,
          ),
          discountValue: new UntypedFormControl(loadedValues?.discountConfiguration?.discountValue ?? 0),
          labelText: new UntypedFormControl(loadedValues?.discountConfiguration?.labelText ?? ''),
          labelColor: new UntypedFormControl(loadedValues?.discountConfiguration?.labelColor ?? 'info'),
          mode: new UntypedFormControl(loadedValues?.discountConfiguration?.mode ?? DiscountMode.Static),
          allowedDiscountValues: new UntypedFormArray(
            (loadedValues?.discountConfiguration?.allowedDiscountValues ?? []).map(createAllowedDiscountValueControl),
          ),
          zeroDiscountLabelText: new UntypedFormControl(
            loadedValues?.discountConfiguration?.zeroDiscountLabelText ?? '',
          ),
        },
        autonomousDiscountValidator,
      ),
      title: new UntypedFormControl(loadedValues?.title ?? 'Upsell'),
      restrictionsRules: this.rulesDynamicFormService.initRulesFormArray(
        loadedValues?.restrictionsRules ?? [],
        getExtensionRules(),
      ),
      whenConditionsNotMet: new UntypedFormControl(
        loadedValues?.whenConditionsNotMet ?? CheckoutUpsellExtension.ConditionsUnmetBehavior.KEEP_IN_CART,
      ),
      style: new UntypedFormGroup({
        displayType: new UntypedFormControl(
          loadedValues?.style?.displayType ?? CheckoutUpsellExtension.DisplayType.CARDS,
          Validators.required,
        ),
        mobileDisplayType: new UntypedFormControl(
          loadedValues?.style?.mobileDisplayType ??
            (loadedValues ? CheckoutUpsellExtension.DisplayType.ROW : CheckoutUpsellExtension.DisplayType.CARDS),
        ),
        buttonAppearance: new UntypedFormControl(loadedValues?.style?.buttonAppearance ?? 'monochrome'),
        numberOfRowsInRowDisplayType: new UntypedFormControl(loadedValues?.style?.numberOfRowsInRowDisplayType ?? 1, [
          Validators.required,
          Validators.min(1),
          Validators.max(5),
        ]),
        carouselNavigationStyle: new UntypedFormControl(
          loadedValues?.style?.carouselNavigationStyle ?? CheckoutUpsellExtension.CarouselNavigationStyle.ARROWS,
        ),
        styleVersion: new UntypedFormControl(loadedValues?.style?.styleVersion ?? null),
        mainBackgroundStyling: new UntypedFormGroup({
          border: new UntypedFormControl(
            this.resolveStyleValue(
              loadedValues,
              'mainBackgroundStyling',
              'border',
              CheckoutExtensionUI.BorderType.NONE,
            ),
          ),
          borderWidth: new UntypedFormControl(
            this.resolveStyleValue(
              loadedValues,
              'mainBackgroundStyling',
              'borderWidth',
              CheckoutExtensionUI.BorderWidth.BASE,
            ),
          ),
          cornerRadius: new UntypedFormControl(
            loadedValues?.style?.mainBackgroundStyling?.cornerRadius === CheckoutExtensionUI.CornerRadius.FULLY_ROUNDED
              ? CheckoutExtensionUI.CornerRadius.LARGE
              : this.resolveStyleValue(
                  loadedValues,
                  'mainBackgroundStyling',
                  'cornerRadius',
                  CheckoutExtensionUI.CornerRadius.NONE,
                ),
          ),
          padding: new UntypedFormControl(
            this.resolveStyleValue(loadedValues, 'mainBackgroundStyling', 'padding', CheckoutExtensionUI.Padding.NONE),
          ),
        }),
        cardBackgroundStyling: new UntypedFormGroup({
          border: new UntypedFormControl(
            this.resolveStyleValue(
              loadedValues,
              'cardBackgroundStyling',
              'border',
              CheckoutExtensionUI.BorderType.NONE,
            ),
          ),
          borderWidth: new UntypedFormControl(
            this.resolveStyleValue(
              loadedValues,
              'cardBackgroundStyling',
              'borderWidth',
              CheckoutExtensionUI.BorderWidth.BASE,
            ),
          ),
          cornerRadius: new UntypedFormControl(
            this.resolveStyleValue(
              loadedValues,
              'cardBackgroundStyling',
              'cornerRadius',
              CheckoutExtensionUI.CornerRadius.NONE,
            ),
          ),
          padding: new UntypedFormControl(
            this.resolveStyleValue(loadedValues, 'cardBackgroundStyling', 'padding', CheckoutExtensionUI.Padding.NONE),
          ),
        }),
        imageStyling: new UntypedFormGroup({
          border: new UntypedFormControl(
            this.resolveStyleValue(loadedValues, 'imageStyling', 'border', CheckoutExtensionUI.BorderType.NONE),
          ),
          borderWidth: new UntypedFormControl(
            this.resolveStyleValue(loadedValues, 'imageStyling', 'borderWidth', CheckoutExtensionUI.BorderWidth.BASE),
          ),
          cornerRadius: new UntypedFormControl(
            this.resolveStyleValue(loadedValues, 'imageStyling', 'cornerRadius', CheckoutExtensionUI.CornerRadius.NONE),
          ),
          padding: new UntypedFormControl(
            this.resolveStyleValue(loadedValues, 'imageStyling', 'padding', CheckoutExtensionUI.Padding.NONE),
          ),
          imageFit: new UntypedFormControl(loadedValues?.style?.imageStyling?.imageFit ?? 'square'),
        }),
      }),
      settings: new UntypedFormGroup(
        {
          thresholdType: new UntypedFormControl(
            loadedValues?.settings?.thresholdType ?? IUpsellThresholdTypes.CartValue,
            Validators.required,
          ),
          onlyCountUpsellItems: new UntypedFormControl(loadedValues?.settings?.onlyCountUpsellItems ?? false),
          textWithThreshold: new UntypedFormControl(
            loadedValues?.settings?.textWithThreshold ??
              this.getTextWithThreshold(loadedValues?.settings?.thresholdType ?? IUpsellThresholdTypes.CartValue),
          ),
          textWithoutThreshold: new UntypedFormControl(
            loadedValues?.settings?.textWithoutThreshold ?? '<h2>You might also like</h2>',
            Validators.required,
          ),
          noThresholdDisplayTitle: new UntypedFormControl(
            loadedValues?.settings?.noThresholdDisplayTitle ??
              loadedValues?.settings?.textWithoutThreshold ??
              '<h2>You might also like</h2>',
          ),
          showMessageIfFreeShippingAvailable: new UntypedFormControl(
            loadedValues?.settings?.showMessageIfFreeShippingAvailable ?? false,
          ),
          textAddToOrder: new UntypedFormControl(
            loadedValues?.settings?.textAddToOrder ?? 'Add to order',
            Validators.required,
          ),
          progressBarColor: new UntypedFormControl(
            loadedValues?.settings?.progressBarColor ?? '#4A7D4A',
            Validators.required,
          ),
          showCompareAtPrice: new UntypedFormControl(loadedValues?.settings?.showCompareAtPrice ?? true),
          variantSelector: new UntypedFormControl(loadedValues?.settings?.variantSelector ?? 'inside'),
          variantButtonText: new UntypedFormControl(loadedValues?.settings?.variantButtonText ?? ''),
          showDiscountBadgeOrText: new UntypedFormControl(loadedValues?.settings?.showDiscountBadgeOrText ?? false),
          discountBadgeDisplayType: new UntypedFormControl(loadedValues?.settings?.discountBadgeDisplayType ?? 'badge'),
          discountBadgeBackgroundColor: new UntypedFormControl(
            loadedValues?.settings?.discountBadgeBackgroundColor ?? '#fd3458',
          ),
          discountBadgeTextColor: new UntypedFormControl(loadedValues?.settings?.discountBadgeTextColor ?? '#ffffff'),
          discountBadgeCornerRadius: new UntypedFormControl(
            loadedValues?.settings?.discountBadgeCornerRadius ?? CheckoutExtensionUI.CornerRadius.NONE,
          ),
          productReviews: createProductReviewsFormGroup(
            loadedValues?.settings?.productReviews,
            this.rulesDynamicFormService,
          ),
          additionalDisplayTexts: new UntypedFormArray(
            (
              loadedValues?.settings?.additionalDisplayTexts ?? [
                { type: 'product-type', display: false, text: 'Type: {product-type}', priority: 0 },
                { type: 'vendor', display: false, text: 'By: {vendor}', priority: 1 },
              ]
            ).map((x) => {
              return new UntypedFormGroup({
                type: new UntypedFormControl(x.type, Validators.required),
                display: new UntypedFormControl(x.display, Validators.required),
                text: new UntypedFormControl(x.text, (control) => {
                  const replaceVariable = '{' + x.type + '}';
                  if (!control?.value?.includes(replaceVariable)) {
                    return { additionalPattern: `${replaceVariable} must be included` };
                  }
                  return null;
                }),
                priority: new UntypedFormControl(x.priority, Validators.required),
              });
            }),
          ),
        },
        (control) => {
          return this.validateTextWithThreshold(control.get('thresholdType').value, control.get('textWithThreshold'));
        },
      ),
      goalsConfiguration: new UntypedFormGroup({
        displayMode: new UntypedFormControl(
          loadedValues?.goalsConfiguration?.displayMode ?? GoalsDisplayMode.SEQUENTIAL,
        ),
        showPreviousGoals: new UntypedFormControl(loadedValues?.goalsConfiguration?.showPreviousGoals ?? false),
        border: new UntypedFormControl(loadedValues?.goalsConfiguration?.border ?? 'none'),
        borderWidth: new UntypedFormControl(loadedValues?.goalsConfiguration?.borderWidth ?? 'base'),
        cornerRadius: new UntypedFormControl(loadedValues?.goalsConfiguration?.cornerRadius ?? 'base'),
        padding: new UntypedFormControl(loadedValues?.goalsConfiguration?.padding ?? 'none'),
        progressBarColor: new UntypedFormControl(loadedValues?.goalsConfiguration?.progressBarColor ?? '#4A7D4A'),
        progressBarLabelSize: new UntypedFormControl(
          loadedValues?.goalsConfiguration?.progressBarLabelSize ?? CheckoutExtensionUI.TextSize.EXTRA_SMALL,
        ),
        goals: new UntypedFormControl(loadedValues?.goalsConfiguration?.goals ?? []),
      }),
      configure: new UntypedFormGroup({
        freeThresholds: new UntypedFormArray(
          loadedValues?.configure?.freeThresholds?.map((thresholdConfig) => {
            const countriesAndRegionsValue = thresholdConfig?.countriesAndRegions || { [this.defaultCountry]: [] };
            return new UntypedFormGroup({
              countriesAndRegions: new UntypedFormControl(countriesAndRegionsValue),
              threshold: new UntypedFormControl(thresholdConfig?.threshold ?? 0),
              currency: new UntypedFormControl(thresholdConfig?.currency ?? this.defaultCurrency),
            });
          }) ?? [],
        ),
      }),
      productSelection: new UntypedFormArray(
        (loadedValues?.productSelection?.length > 0
          ? loadedValues.productSelection
          : [{ ...DEFAULT_RULE_GROUP, id: 0 }]
        ).map((ruleGroup, index: number) => createRuleGroupFormGroup(index, ruleGroup, this.rulesDynamicFormService)),
      ),
    });

    this.dispatchExampleViewValues(this.formGroup.value);
    refreshAllowedDiscountValueValidity(this.allowedDiscountValuesFormArray);

    this.formGroup
      .get('settings.thresholdType')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((thresholdType) => {
        const newText = this.getTextWithThreshold(thresholdType);

        const textWithThresholdControl = this.formGroup.get('settings.textWithThreshold');
        if (textWithThresholdControl && thresholdType) {
          textWithThresholdControl.setValue(newText);
        }

        if (this.goals?.length && thresholdType) {
          this.goals = this.goals.map((goal) => ({
            ...goal,
            textWithThreshold: newText,
          }));
          this.updateGoalsInForm();
        }
      });

    this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((values) => {
      this.dispatchExampleViewValues(values);
    });

    this.resetAutonomousWhenLegacyDiscountUnchecked();

    this.syncStylingWithBorder();

    this.initGoals(loadedValues);

    this.goals.forEach((goal) => {
      if (
        goal.offerType === GiftUnlockExtension.OfferType.COUPON ||
        goal.offerType === GiftUnlockExtension.OfferType.PRODUCT
      ) {
        this.loadGiftUnlockExtensions(goal.offerType);
      }
    });
  }

  // a missing field on a pre-styleVersion upsell falls back to what the extension renders, so saving keeps its look
  private resolveStyleValue(loadedValues: any, group: string, field: string, defaultValue: string): string {
    const storedValue = loadedValues?.style?.[group]?.[field];
    if (storedValue !== undefined) {
      return storedValue;
    }
    if (loadedValues && loadedValues.style?.styleVersion === undefined) {
      return LEGACY_STYLE_FALLBACKS[group]?.[field] ?? defaultValue;
    }
    return defaultValue;
  }

  // The legacy checkbox hides the autonomous toggle, so unchecking it has to take the upsell back to a static discount
  private resetAutonomousWhenLegacyDiscountUnchecked(): void {
    this.formGroup
      .get('applyDiscountToRecommendedProducts')
      .valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((applyDiscount: boolean) => {
        if (!applyDiscount && this.isAutonomousDiscount) {
          this.resetToStaticDiscount();
        }
      });
  }

  // A border needs spacing around it to look right, and without a border padding has nothing to sit against
  private syncStylingWithBorder(): void {
    STYLING_GROUP_NAMES.forEach((groupName) => {
      const stylingGroup = this.formGroup.get(`style.${groupName}`);

      stylingGroup
        .get('border')
        .valueChanges.pipe(takeUntil(this.destroy$))
        .subscribe((border: CheckoutExtensionUI.BorderType) => {
          const dependentStyling =
            border === CheckoutExtensionUI.BorderType.NONE
              ? { padding: CheckoutExtensionUI.Padding.NONE }
              : {
                  borderWidth: CheckoutExtensionUI.BorderWidth.BASE,
                  cornerRadius: CheckoutExtensionUI.CornerRadius.BASE,
                  padding: CheckoutExtensionUI.Padding.SMALL500,
                };

          stylingGroup.patchValue(dependentStyling, { emitEvent: false });
        });
    });
  }

  private validateTextWithThreshold(thresholdType: string, control: AbstractControl): ValidationErrors {
    if (thresholdType === IUpsellThresholdTypes.NoThreshold) {
      return null;
    }

    const variableToCheck =
      thresholdType === IUpsellThresholdTypes.ItemCount
        ? UpsellFreeShippingThresholdReplaceValues.ItemsLeft
        : UpsellFreeShippingThresholdReplaceValues.AmountLeft;

    return control.value.includes(variableToCheck) ? null : { pattern: `${variableToCheck} must be included` };
  }

  private getTextWithThreshold(thresholdType: string): any {
    if (thresholdType === IUpsellThresholdTypes.ItemCount) {
      return `<h2>Add ${UpsellFreeShippingThresholdReplaceValues.ItemsLeft} more items, get Free Shipping!</h2>`;
    }
    return `<h2>Spend ${UpsellFreeShippingThresholdReplaceValues.AmountLeft} more, get Free Shipping!</h2>`;
  }

  addAllowedDiscountValue() {
    this.allowedDiscountValuesFormArray.push(createAllowedDiscountValueControl(0));
    this.formGroup.markAsDirty();
  }

  removeAllowedDiscountValue(index: number) {
    this.allowedDiscountValuesFormArray.removeAt(index);
    this.formGroup.markAsDirty();
  }

  onAutonomousDiscountToggled(enabled: boolean) {
    if (enabled) {
      const discountConfiguration = this.formGroup.get('discountConfiguration');
      const discountType = discountConfiguration.get('discountType');
      const discountValue = discountConfiguration.get('discountValue');

      discountConfiguration.get('mode').setValue(DiscountMode.Autonomous, { emitEvent: false });
      this.staticDiscountSnapshot = { discountType: discountType.value, discountValue: discountValue.value };

      this.formGroup.get('applyDiscountToRecommendedProducts').setValue(true, { emitEvent: false });
      discountValue.setValue(resolveAutonomousFallbackValue(discountType.value, discountValue.value), {
        emitEvent: false,
      });
      discountType.setValue(CheckoutUpsellExtension.DiscountType.Percentage, { emitEvent: false });
      this.prefillAllowedDiscountValues();

      refreshAllowedDiscountValueValidity(this.allowedDiscountValuesFormArray);
      this.refreshDiscountSectionVisibility();
      this.dispatchExampleViewValues(this.formGroup.value);
    } else {
      this.resetToStaticDiscount();
    }

    this.formGroup.markAsDirty();

    this.analytics.capture(AnalyticsEvent.AutonomousUpsellDiscountToggled, {
      routePattern: this.analytics.currentRoutePattern,
      enabled,
      extensionId: this.loadedExtension?.id,
    });
  }

  private resetToStaticDiscount(): void {
    const discountConfiguration = this.formGroup.get('discountConfiguration');
    discountConfiguration.get('mode').setValue(DiscountMode.Static, { emitEvent: false });

    if (this.staticDiscountSnapshot) {
      const { discountType, discountValue } = this.staticDiscountSnapshot;
      discountConfiguration.get('discountType').setValue(discountType, { emitEvent: false });
      discountConfiguration.get('discountValue').setValue(discountValue, { emitEvent: false });
      this.staticDiscountSnapshot = null;
    }
    if (!this.showLegacyDiscountControls) {
      this.formGroup.get('applyDiscountToRecommendedProducts').setValue(false, { emitEvent: false });
    }

    refreshAllowedDiscountValueValidity(this.allowedDiscountValuesFormArray);
    this.refreshDiscountSectionVisibility();
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  private refreshDiscountSectionVisibility(): void {
    const isAutonomous = this.isAutonomousDiscount;
    this.showLegacyDiscountControls = resolveShowLegacyDiscountControls(this.loadedExtension?.template);
    this.showStaticDiscountControls = resolveShowStaticDiscountControls(isAutonomous, this.autonomousDiscountFlagOn);
    this.showDiscountSection = resolveShowDiscountSection(
      this.showLegacyDiscountControls,
      isAutonomous,
      this.autonomousDiscountFlagOn,
    );
  }

  private prefillAllowedDiscountValues(): void {
    const allowedDiscountValues = this.allowedDiscountValuesFormArray;
    resolveAllowedDiscountValuesOnEnable(allowedDiscountValues.value ?? [])
      .slice(allowedDiscountValues.length)
      .forEach((value) => allowedDiscountValues.push(createAllowedDiscountValueControl(value), { emitEvent: false }));
  }

  handleSave() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
    }
    this.goalsComponent?.syncAllGoalThresholds();

    if (this.goalsComponent && !this.goalsComponent.validateThresholdOrder()) {
      return;
    }

    const formValue = JSON.parse(JSON.stringify(this.formGroup.value));
    removeDiscountRulesWithoutEntity(formValue.productSelection);
    formValue.discountConfiguration = prepareDiscountConfigurationForSave(
      formValue.discountConfiguration as AutonomousDiscountConfiguration,
      this.autonomousDiscountFlagOn,
    );
    if (!this.loadedExtension?.id) {
      formValue.style.mobileDisplayType = formValue.style.displayType;
      // new or duplicated upsells should get the new upsell style version.
      formValue.style.styleVersion = CHECKOUT_UPSELL_STYLE_VERSION;
    }
    // preserving old behaviour for old upsells.
    if (formValue.style.styleVersion == null) {
      delete formValue.style.styleVersion;
    }

    const { usageToken, ...template } = formValue;
    const timerConfiguration = this.timerComponent?.getTimerConfiguration() ?? this.currentTimerConfiguration;
    const payload: Partial<ICheckoutExtensions> = {
      template: { ...template, timerConfiguration },
      usageToken,
      type: ExtensionComponentType.CHECKOUT_UPSELL,
    };

    if (this.loadedExtension?.id) {
      const id = this.loadedExtension.id;
      this.store.dispatch(CheckoutActions.updateExtension({ id, extension: payload }));
    } else {
      this.store.dispatch(CheckoutActions.saveExtension({ extension: payload }));
    }
  }

  addFreeShippingThreshold() {
    this.freeThresholdsFormArray.push(
      new UntypedFormGroup({
        countriesAndRegions: new UntypedFormControl({ [this.defaultCountry]: [] }),
        threshold: new UntypedFormControl(0),
        currency: new UntypedFormControl(this.defaultCurrency),
      }),
    );
  }

  removeFreeShippingThreshold(index: number) {
    this.freeThresholdsFormArray.removeAt(index);
  }

  handleTimerConfigurationChange = () => {
    this.currentTimerConfiguration = this.timerComponent?.getTimerConfiguration() ?? null;
    this.cdr.detectChanges();
    this.dispatchExampleViewValues(this.formGroup.value);
  };

  loadGiftUnlockExtensions(offerType: string) {
    this.extensionsService
      .getGiftUnlocksByOfferType(offerType)
      .pipe(takeUntil(this.destroy$))
      .subscribe((extensions: ICheckoutExtensions[]) => {
        extensions.forEach((ext) => this.giftUnlockExtensionsMap.set(ext.id, ext));
        this.giftUnlockExtensionsByOfferType = {
          ...this.giftUnlockExtensionsByOfferType,
          [offerType]: extensions.map((ext) => ({
            label: ext.template?.internalName || 'Unnamed Extension',
            value: ext.id,
          })),
        };
      });
  }

  onOfferTypeChange(offerType: string) {
    if (offerType === GiftUnlockExtension.OfferType.COUPON || offerType === GiftUnlockExtension.OfferType.PRODUCT) {
      this.loadGiftUnlockExtensions(offerType);
    }
  }

  createDefaultGoal(): Goal {
    return {
      id: generateRandomString(12),
      enabled: true,
      offerType: '',
      selectedGiftUnlockExtension: null,
      textWithThreshold: this.getTextWithThreshold(
        this.formGroup?.get('settings.thresholdType')?.value ?? IUpsellThresholdTypes.CartValue,
      ),
      textWithoutThreshold: '<h2>You might also like</h2>',
      showMessageIfFreeShippingAvailable: false,
      showInfoInProgressBar: false,
      progressBarLabel: '',
      iconUrl: '',
      thresholds: [],
    };
  }

  addGoal() {
    this.goals = [...this.goals, this.createDefaultGoal()];
    this.updateGoalsInForm();
  }

  removeGoal(goalIndex: number) {
    if (goalIndex > 0) {
      this.goals = this.goals.filter((_, index) => index !== goalIndex);
      if (this.goals.length === 1) {
        this.goals = [{ ...this.goals[0], enabled: true }];
      }
      this.updateGoalsInForm();
    }
  }

  onGoalsChange(goals: Goal[]) {
    this.goals = goals;
    this.updateGoalsInForm();
  }

  onPreviewTextChange(event: { goalIndex: number; textType: UpsellTextType }) {
    this.previewedGoalIndex = event.goalIndex;
    this.previewedTextType = event.textType;
    this.dispatchExampleViewValues(this.formGroup.value);
  }

  private updateGoalsInForm() {
    this.formGroup.get('goalsConfiguration')?.patchValue({ goals: this.goals });
  }

  private initGoals(loadedValues?: any) {
    if (loadedValues?.goalsConfiguration?.goals?.length) {
      this.goals = loadedValues.goalsConfiguration.goals.map((goal: Goal) => ({
        ...goal,
        id: goal.id || generateRandomString(12),
      }));
    } else {
      this.goals = [this.createDefaultGoal()];
    }
    this.updateGoalsInForm();
  }
}
